import { can } from '@batiflow/client-core';
import { useActiveMembership } from '@batiflow/client-core/react';
import { Button } from '@fluentui/react-components';
import { DocumentSignature20Regular } from '@fluentui/react-icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RequestSignatureDialog } from '@/features/signatures';
import type { DocumentDetail } from './document-model';
import { usePanelStyles } from './panel-styles';

export interface SignatureSectionProps {
  /** Null in another organisation's space: signers are members of the caller's own organisation. */
  orgId: string | null;
  document: DocumentDetail;
}

/**
 * "Demander une signature" in the document panel (SIGN-FE-01), for who holds `signature.request`:
 * the current version of the document goes to members of the office to sign. The file must be
 * available (checked) before a request can be made; the API says so too.
 */
export function SignatureSection({ orgId, document }: SignatureSectionProps) {
  const { t } = useTranslation('signatures');
  const panel = usePanelStyles();
  const membership = useActiveMembership();
  const [open, setOpen] = useState(false);
  if (orgId === null || !can(membership?.permissions, 'signature.request')) return null;
  if (document.currentVersionId === null) return null;
  const ready = document.latestVersionStatus === 'available';
  return (
    <section className={panel.section} aria-label={t('section.region')}>
      <div className={panel.sectionHead}>
        <h3 className={panel.heading}>{t('section.title')}</h3>
        <Button
          size="small"
          appearance="secondary"
          icon={<DocumentSignature20Regular />}
          disabledFocusable={!ready}
          onClick={() => {
            setOpen(true);
          }}
        >
          {t('create.action')}
        </Button>
      </div>
      <p className={panel.muted}>{ready ? t('section.hint') : t('section.notReady')}</p>
      {open ? (
        <RequestSignatureDialog
          orgId={orgId}
          document={{ id: document.id, title: document.title }}
          onClose={() => {
            setOpen(false);
          }}
        />
      ) : null}
    </section>
  );
}
